/* «Acerca de SGR»: identidad, autoría, herramientas de IA usadas durante el desarrollo, datos técnicos
 * de soporte y, en una segunda vista, el inventario de software de terceros.
 *
 * Los datos técnicos salen de sus fuentes canónicas (/config.json que genera sgr-deploy y GET /meta):
 * nada está escrito a mano. El inventario de licencias se descarga solo cuando se abre esa vista. */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { Button, Input } from '@/shared/ui';
import { BrandLogo } from '@/shared/ui/brand';
import { get } from '@/shared/api/client';
import { useAppConfig } from '@/app/config';
import { BRAND_ASSETS, BrandMark } from './BrandMark';
import s from './about.module.css';

const AUTHOR = 'Gonzalo Martínez Placencia';

type Meta = { version: string; env: string; sgo: { enabled: boolean; contract_version: string } };
type Component = { name: string; version: string; license: string; copyright?: string | null; part: 'frontend' | 'backend'; textId?: string };
type Notices = { sgrVersion: string; components: Component[]; texts: Record<string, string> };

const ENV_LABEL: Record<string, string> = { dev: 'Desarrollo', test: 'Pruebas', staging: 'Staging', production: 'Producción' };

/** "2026.8.0+abc1234" → versión y build por separado; el build es el commit corto, dato público de la release. */
function splitVersion(v: string): { version: string; build: string | null } {
  const i = v.indexOf('+');
  return i < 0 ? { version: v, build: null } : { version: v.slice(0, i), build: v.slice(i + 1) };
}

export function AboutDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [view, setView] = useState<'about' | 'licenses'>('about');
  useEffect(() => { if (!open) setView('about'); }, [open]);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.panel} aria-describedby={undefined} data-testid="about-dialog">
          {view === 'about' ? <AboutView onLicenses={() => setView('licenses')} /> : <LicensesView onBack={() => setView('about')} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CloseButton() {
  return (
    <Dialog.Close asChild>
      <button type="button" aria-label="Cerrar" title="Cerrar" style={{ background: 'none', border: 0, cursor: 'pointer', fontSize: 20, lineHeight: 1, color: 'var(--sgr-text-secondary)', minWidth: 44, minHeight: 44 }}>✕</button>
    </Dialog.Close>
  );
}

function AboutView({ onLicenses }: { onLicenses: () => void }) {
  const cfg = useAppConfig();
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => get<Meta>('/meta'), staleTime: 5 * 60_000, retry: false });
  // /config.json es la fuente canónica de la release desplegada ("2026.8.0+abc1234", lo escribe sgr-deploy);
  // el backend solo aporta la versión del paquete y el contrato SGO.
  const { version, build } = splitVersion(cfg.version !== 'dev' ? cfg.version : (meta.data?.version ?? cfg.version));
  const env = meta.data?.env ?? cfg.env;
  return (
    <>
      <div className={s.head}>
        <BrandLogo height={22} />
        <CloseButton />
      </div>
      <div className={s.body}>
        <Dialog.Title asChild><h2 className={s.name}>SGR</h2></Dialog.Title>
        <p className={s.subtitle}>Sistema de Gestión de Rendiciones</p>
        <p className={s.org}>PASCOM · 2026</p>
        <p className={s.lead}>
          Plataforma para la gestión integral de solicitudes de fondos, viáticos, rendiciones, gastos,
          revisiones y restituciones.
        </p>

        <section className={s.section}>
          <h3 className={s.sectionTitle}>Concepción, diseño funcional y código</h3>
          <p className={s.author}>{AUTHOR}</p>
          <p className={s.authorRole}>Definición funcional, reglas de negocio y dirección del proyecto.</p>
        </section>

        <section className={s.section}>
          <h3 className={s.sectionTitle}>Desarrollo y operación asistido por inteligencia artificial</h3>
          <p className={s.lead} style={{ marginTop: 0 }}>
            SGR fue desarrollado mediante un proceso de ingeniería asistida por inteligencia artificial,
            utilizada como herramienta de apoyo para implementación, análisis, arquitectura, pruebas y
            documentación.
          </p>
          <div className={s.tools}>
            <div className={s.tool}>
              <BrandMark src={BRAND_ASSETS.claudeCode} name="Claude Code" height={20} />
              <div><div className={s.toolName}>Claude / Claude Code</div><div className={s.toolOrg}>Anthropic</div></div>
            </div>
            <div className={s.tool}>
              <BrandMark src={BRAND_ASSETS.openai} name="OpenAI" height={18} />
              <div><div className={s.toolName}>ChatGPT</div><div className={s.toolOrg}>OpenAI</div></div>
            </div>
          </div>
          <p className={s.authorRole} style={{ marginTop: 'var(--sp-3)' }}>
            Marcas de sus respectivos titulares. Su mención describe herramientas utilizadas durante el
            desarrollo y no implica patrocinio, certificación ni respaldo de SGR.
          </p>
        </section>

        <section className={s.section}>
          <h3 className={s.sectionTitle}>Información técnica</h3>
          <dl className={s.meta}>
            <dt>Versión</dt><dd data-testid="about-version">{version}</dd>
            {build && (<><dt>Build</dt><dd data-testid="about-build">{build}</dd></>)}
            <dt>Entorno</dt><dd>{ENV_LABEL[env] ?? env}</dd>
            {meta.data && (<><dt>Integración SGO</dt><dd>{meta.data.sgo.enabled ? `Contrato v${meta.data.sgo.contract_version}` : 'No configurada'}</dd></>)}
          </dl>
        </section>
      </div>
      <div className={s.foot}>
        <Button type="button" size="sm" variant="secondary" onClick={onLicenses} data-testid="about-licenses">Licencias de terceros</Button>
        <Dialog.Close asChild><Button type="button" size="sm" variant="secondary">Cerrar</Button></Dialog.Close>
      </div>
    </>
  );
}

function LicensesView({ onBack }: { onBack: () => void }) {
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { backRef.current?.focus(); }, []);
  const notices = useQuery({
    queryKey: ['third-party-notices'],
    queryFn: async () => {
      const r = await fetch('/third-party.json', { cache: 'force-cache' });
      if (!r.ok) throw new Error('no disponible');
      return (await r.json()) as Notices;
    },
    staleTime: Infinity,
    retry: false,
  });
  const all = useMemo(() => notices.data?.components ?? [], [notices.data]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? all.filter((c) => c.name.toLowerCase().includes(t) || c.license.toLowerCase().includes(t)) : all;
  }, [all, q]);
  return (
    <>
      <div className={s.head}>
        <Dialog.Title asChild><h2 style={{ margin: 0, fontSize: 'var(--fs-md)' }}>Software de código abierto</h2></Dialog.Title>
        <CloseButton />
      </div>
      <div className={s.body}>
        <p className={s.lead} style={{ marginTop: 0 }}>
          SGR se distribuye junto con estos componentes de terceros. Cada uno conserva su propia licencia
          y sus avisos de copyright. Las herramientas usadas solo para desarrollar o construir el sistema
          no se distribuyen y no se listan aquí.
        </p>
        {notices.isPending && <p className={s.authorRole}>Cargando inventario…</p>}
        {notices.isError && <p className={s.authorRole}>No fue posible cargar el inventario de licencias.</p>}
        {notices.data && (
          <>
            <div style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar componente o licencia" aria-label="Buscar componente o licencia" inputMode="search" />
            </div>
            <p className={s.authorRole} data-testid="licenses-count">{shown.length} de {all.length} componentes</p>
            <ul className={s.list} data-testid="licenses-list">
              {shown.map((c) => (
                <li key={`${c.part}-${c.name}`} className={s.item}>
                  <div className={s.itemHead}>
                    <span className={s.itemName}>{c.name}</span>
                    <span className={s.itemLic}>{c.license}</span>
                  </div>
                  <div className={s.itemVer}>Versión {c.version} · {c.part === 'frontend' ? 'Frontend' : 'Backend'}</div>
                  {c.copyright && <p className={s.itemCopy}>{c.copyright}</p>}
                  {c.textId && (
                    <>
                      <button type="button" className={s.textBtn} aria-expanded={openId === `${c.part}-${c.name}`} onClick={() => setOpenId(openId === `${c.part}-${c.name}` ? null : `${c.part}-${c.name}`)}>
                        {openId === `${c.part}-${c.name}` ? 'Ocultar texto de licencia' : 'Ver texto de licencia'}
                      </button>
                      {openId === `${c.part}-${c.name}` && <pre className={s.licText}>{notices.data.texts[c.textId]}</pre>}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className={s.foot}>
        <Button ref={backRef} type="button" size="sm" variant="secondary" onClick={onBack}>← Volver</Button>
        <Dialog.Close asChild><Button type="button" size="sm" variant="secondary">Cerrar</Button></Dialog.Close>
      </div>
    </>
  );
}
